import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { VentasService } from "../services/ventasService";
import { SucursalService } from "../services/sucursalService.js";
import { EstadoSucursalService } from "../services/estadoSucursalService.js";
import { ParametroService } from "../services/parametroService.js";
import RolesList from '../components/roles/RolesList';
import CategoriaList from '../components/categorias/CategoriaList';
import ProductoList from '../components/productos/ProductoList';
import PagoClienteList from '../components/PagoClientes/PagoClienteList';
import DescuentoList from '../components/descuentos/DescuentoList';
import ProductoOpcionesModal from '../components/productos/ProductoOpcionesModal'; // ✅ NUEVO
import theme, { cn } from '../styles/theme';
import { API_CONFIG } from '../utils/constants';

const Dashboard = () => {
  const { user, logout, getSucursalId } = useAuth();
  const [activeSection, setActiveSection] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [sucursal, setSucursal] = useState(null);
  const [estadoSucursal, setEstadoSucursal] = useState(null);
  const [parametro, setParametro] = useState([]);
  const [isTogglingEstado, setIsTogglingEstado] = useState(false);

  const [pedidos, setPedidos] = useState([]);
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
  const [tiempoPreparacion, setTiempoPreparacion] = useState({});
  const [tiemposRestantes, setTiemposRestantes] = useState({});
  const [procesando, setProcesando] = useState({});
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [audioHabilitado, setAudioHabilitado] = useState(false);
  const [limiteVenta, setLimiteVenta] = useState({ limite: 0, consumido: 0, limiteSecundario: 0 });
  const [imagenAmpliada, setImagenAmpliada] = useState(null);

  // ✅ NUEVO: modal opciones producto
  const [opcionesModal, setOpcionesModal] = useState(null);

  // ─── REPORTE STATE ─────────────────────────────────────────────────────────
  const [reporteData, setReporteData] = useState(null);
  const [loadingReporte, setLoadingReporte] = useState(false);
  const [reporteFechaInicio, setReporteFechaInicio] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [reporteFechaFin, setReporteFechaFin] = useState(() => new Date().toISOString().split('T')[0]);
  const [reporteOrdenExpanded, setReporteOrdenExpanded] = useState(null);

  const audioRef = useRef(null);
  const pedidosAnterioresRef = useRef([]);
  const intervalRef = useRef(null);
  const sucursalId = getSucursalId();

  // ── HELPERS ────────────────────────────────────────────────────────────────

  // ✅ NUEVO: parsear opciones_json
  const parsearOpciones = (opcionesJson) => {
    if (!opcionesJson) return [];
    try {
      const lista = JSON.parse(opcionesJson);
      return Array.isArray(lista) ? lista : [];
    } catch { return []; }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const habilitarAudio = () => {
    if (!audioHabilitado && audioRef.current) {
      audioRef.current.play()
        .then(() => { audioRef.current.pause(); audioRef.current.currentTime = 0; setAudioHabilitado(true); })
        .catch(err => console.warn('⚠️ No se pudo habilitar el audio:', err));
    }
  };

  const reproducirSonido = () => {
    if (audioRef.current && audioHabilitado) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.warn('⚠️ Error al reproducir sonido:', err));
    }
  };

  const calcularTiempoRestante = (fechaModi, horaModi, tiempoTotal) => {
    try {
      if (!fechaModi || !horaModi || !tiempoTotal) return null;
      let fechaBase;
      if (typeof fechaModi === 'string' && fechaModi.includes('T')) fechaBase = fechaModi.split('T')[0];
      else if (typeof fechaModi === 'string' && fechaModi.includes('-')) fechaBase = fechaModi;
      else return null;
      const fechaModificacion = new Date(`${fechaBase}T${horaModi}`);
      if (isNaN(fechaModificacion.getTime())) return null;
      const diferenciaMs = new Date(fechaModificacion.getTime() + tiempoTotal * 60000) - new Date();
      if (diferenciaMs <= 0) return { minutos: 0, segundos: 0, total: 0 };
      return { minutos: Math.floor(diferenciaMs / 60000), segundos: Math.floor((diferenciaMs % 60000) / 1000), total: Math.floor(diferenciaMs / 1000) };
    } catch { return null; }
  };

  const formatearTiempoRestante = (t) => {
    if (!t || t.total <= 0) return '00:00';
    return `${String(t.minutos).padStart(2,'0')}:${String(t.segundos).padStart(2,'0')}`;
  };

  const getParametroValue = (nombre) => {
    if (!parametro?.length) return false;
    const p = parametro.find(p => (p.Nombre || p.nombre) === nombre);
    if (!p) return false;
    const v = p.Valor || p.valor;
    return v === true || v === 'true' || v === 'TRUE' || v === '1' || v === 1;
  };

  const getParametroNumerico = (nombre, def = 30) => {
    if (!parametro?.length) return def;
    const p = parametro.find(p => (p.Nombre || p.nombre) === nombre);
    if (!p) return def;
    const n = parseInt(p.Valor || p.valor);
    return isNaN(n) ? def : n;
  };

  const getTelefonoSoporte = () => {
    if (!parametro?.length) return null;
    const p = parametro.find(p => (p.Nombre || p.nombre) === 'TLF_SOPORTE');
    return p ? (p.Valor || p.valor) : null;
  };

  const extraerLimiteVenta = (params = parametro) => {
    if (!params?.length) { setLimiteVenta({ limite: 0, consumido: 0, limiteSecundario: 0 }); return; }
    const p = params.find(p => (p.Nombre || p.nombre) === 'LIMITE_VENTA');
    if (!p) { setLimiteVenta({ limite: 0, consumido: 0, limiteSecundario: 0 }); return; }
    const consumido = parseFloat(p.Valor2 || p.valor2 || 0);
    const tipo = p.Tipo || p.tipo || '';
    const limites = tipo.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
    setLimiteVenta({ limite: limites[0] || 0, consumido, limiteSecundario: limites[1] || 0 });
  };

  const loadPedidos = async () => {
    if (!sucursalId) return;
    try {
      setLoadingPedidos(true);
      const response = await VentasService.getVentasBySucursal(sucursalId);
      if (response.success && response.data) {
        const ventasArray = Array.isArray(response.data) ? response.data : [response.data];
        const pedidosActivos = ventasArray.filter(v =>
          v.venta && v.venta.estado === 'ACTIVO' && ['CREADO', 'ACEPTADO', 'TERMINADO', 'EN_LOCAL'].includes(v.venta.estatus)
        );
        setPedidos(pedidosActivos);
        const nuevos = pedidosActivos.filter(p =>
          !pedidosAnterioresRef.current.find(a => a.venta.id_ventas === p.venta.id_ventas) && p.venta.estatus === 'CREADO'
        );
        if (nuevos.length > 0) reproducirSonido();
        pedidosAnterioresRef.current = pedidosActivos;
      }
    } catch (e) { console.error('❌ Error al cargar pedidos:', e); }
    finally { setLoadingPedidos(false); }
  };

  const loadReporte = async () => {
    if (!sucursalId) return;
    setLoadingReporte(true);
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTE_ORDENES_SUCURSAL}${sucursalId}?fechaInicio=${reporteFechaInicio}&fechaFin=${reporteFechaFin}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) setReporteData(data.data);
      else console.error('Error reporte:', data.message);
    } catch (e) { console.error('❌ Error cargando reporte:', e); }
    finally { setLoadingReporte(false); }
  };

  const handleAceptarPedido = async (idVenta) => {
    const tiempo = tiempoPreparacion[idVenta];
    if (!tiempo || tiempo <= 0) { alert('⚠️ Por favor ingresa un tiempo de preparación válido'); return; }
    setProcesando(prev => ({ ...prev, [idVenta]: true }));
    try {
      await VentasService.updateEstatus(idVenta, 'ACEPTADO');
      await VentasService.updateTiempo(idVenta, tiempo);
      await loadPedidos();
      setPedidoSeleccionado(null);
      setTiempoPreparacion(prev => { const n = { ...prev }; delete n[idVenta]; return n; });
    } catch (e) { alert('❌ Error al aceptar: ' + e.message); }
    finally { setProcesando(prev => ({ ...prev, [idVenta]: false })); }
  };

  const handleCancelarPedido = async (idVenta) => {
    if (!window.confirm('¿Cancelar este pedido?')) return;
    setProcesando(prev => ({ ...prev, [idVenta]: true }));
    try { await VentasService.updateEstatus(idVenta, 'CANCELADO'); await loadPedidos(); setPedidoSeleccionado(null); }
    catch (e) { alert('❌ Error al cancelar: ' + e.message); }
    finally { setProcesando(prev => ({ ...prev, [idVenta]: false })); }
  };

  const handleTerminarPedido = async (idVenta) => {
    if (!window.confirm('¿Marcar como TERMINADO?')) return;
    setProcesando(prev => ({ ...prev, [idVenta]: true }));
    try { await VentasService.updateEstatus(idVenta, 'TERMINADO'); await loadPedidos(); setPedidoSeleccionado(null); }
    catch (e) { alert('❌ Error: ' + e.message); }
    finally { setProcesando(prev => ({ ...prev, [idVenta]: false })); }
  };

  const getEstatusColor = (estatus) => {
    const m = { CREADO: 'bg-yellow-100 text-yellow-800 border-yellow-300', ACEPTADO: 'bg-blue-100 text-blue-800 border-blue-300', TERMINADO: 'bg-purple-100 text-purple-800 border-purple-300', EN_LOCAL: 'bg-orange-100 text-orange-800 border-orange-300', EN_CAMINO: 'bg-indigo-100 text-indigo-800 border-indigo-300', ENTREGADO: 'bg-green-100 text-green-800 border-green-300', CANCELADO: 'bg-red-100 text-red-800 border-red-300' };
    return m[estatus] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatearHora = (hora) => hora ? hora.substring(0, 5) : '';
  const formatearFecha = (f) => { if (!f) return ''; const d = new Date(f); return isNaN(d) ? f : d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' }); };

  const handleWhatsAppClick = () => {
    habilitarAudio();
    const telefono = getTelefonoSoporte();
    if (!telefono) { alert('⚠️ No se ha configurado el número de soporte.'); return; }
    window.open(`https://wa.me/${telefono.replace(/[^\d+]/g,'')}?text=${encodeURIComponent('Hola, necesito soporte.')}`, '_blank');
  };

  const handleEstadoChange = async (e) => {
    habilitarAudio();
    const nuevoEstado = e.target.value;
    if (!sucursalId || isTogglingEstado) return;
    setIsTogglingEstado(true);
    try {
      const dataToSend = { Id_sucursal: sucursalId, Estado: nuevoEstado };
      if (nuevoEstado === 'ONLINE') dataToSend.Observa = 'ONLINE';
      const result = await EstadoSucursalService.create(dataToSend);
      if (result?.success) setEstadoSucursal({ ...estadoSucursal, estado: result.data.estado, id_estado: result.data.id_estado });
      else throw new Error('No se pudo actualizar');
    } catch { alert('Error al cambiar el estado'); await loadEstadoSucursal(); }
    finally { setIsTogglingEstado(false); }
  };

  const menuItems = [
    { id: 'home',        label: 'Pedidos',    icon: '🛒' },
    { id: 'reporte',     label: 'Reportes',   icon: '📊' },
    { id: 'categoria',   label: 'Categorías', icon: '🏷️', bloqueado: !getParametroValue('BLOQUEO_CATEGORIA') },
    { id: 'producto',    label: 'Productos',  icon: '📦', bloqueado: !getParametroValue('BLOQUEO_PRODUCTO') },
    { id: 'descuento',   label: 'Descuentos', icon: '🎁', bloqueado: !getParametroValue('BLOQUEO_PROMO') },
    { id: 'pagocliente', label: 'Pagos',      icon: '💳' },
  ];

  const loadSucursal      = async () => { if (!sucursalId) return; try { const d = await SucursalService.getById(sucursalId); if (d) setSucursal(d); } catch {} };
  const loadEstadoSucursal= async () => { if (!sucursalId) return; try { const d = await EstadoSucursalService.getById(sucursalId); setEstadoSucursal(d?.estado ? d : null); } catch { setEstadoSucursal(null); } };
  const loadParametro     = async () => { if (!sucursalId) return; try { const d = await ParametroService.getAllPa(sucursalId); const datos = d || []; setParametro(datos); extraerLimiteVenta(datos); } catch { setParametro([]); } };

  useEffect(() => {
    const init = async () => { await Promise.all([loadSucursal(), loadEstadoSucursal(), loadParametro()]); await loadPedidos(); };
    init();
  }, []);

  useEffect(() => {
    const handleClick = () => { habilitarAudio(); document.removeEventListener('click', handleClick); };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    if (activeSection === 'home' && sucursalId) {
      const tiempo = getParametroNumerico('TIEMPO_RECARGA_PEDIDO', 10);
      intervalRef.current = setInterval(() => Promise.all([loadPedidos(), loadParametro()]), tiempo * 1000);
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }
  }, [activeSection, sucursalId]);

  useEffect(() => {
    const pedidosPendientes = pedidos.filter(p => p.venta.estatus === 'CREADO');
    if (pedidosPendientes.length > 0 && activeSection === 'home' && audioHabilitado) {
      const s = setInterval(() => reproducirSonido(), 15000);
      return () => clearInterval(s);
    }
  }, [pedidos, activeSection, audioHabilitado]);

  useEffect(() => {
    const intervalo = setInterval(() => {
      const con = pedidos.filter(p => ['ACEPTADO', 'TERMINADO'].includes(p.venta.estatus) && p.venta.tiempo > 0);
      if (con.length > 0) {
        const nuevos = {};
        con.forEach(p => { const t = calcularTiempoRestante(p.venta.fecha_modi, p.venta.hora_modi, p.venta.tiempo); if (t) nuevos[p.venta.id_ventas] = t; });
        setTiemposRestantes(nuevos);
      }
    }, 1000);
    return () => clearInterval(intervalo);
  }, [pedidos]);

  const handleLogout    = () => { if (window.confirm('¿Cerrar sesión?')) logout(); };
  const handleMenuClick = (id, bloqueado) => { habilitarAudio(); if (bloqueado) { alert('⚠️ Función bloqueada.'); return; } setActiveSection(id); };

  // ─── WIDGET DRIVER ─────────────────────────────────────────────────────────
  const DriverBadge = ({ nombreDriver, tlfDriver, imagen, compact = false }) => {
    if (!nombreDriver) return null;
    if (compact) {
      return (
        <div className="flex flex-col gap-0.5 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center gap-1.5">
            {imagen
              ? <img src={`data:image/jpeg;base64,${imagen}`} alt={nombreDriver} className="w-5 h-5 rounded-full object-cover border border-indigo-300" />
              : <span className="text-sm">🛵</span>}
            <span className="text-xs font-semibold text-indigo-700 truncate max-w-[100px]">{nombreDriver}</span>
          </div>
          {tlfDriver && <p className="text-xs text-indigo-500 pl-0.5">📞 {tlfDriver}</p>}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl">
        {imagen
          ? <img src={`data:image/jpeg;base64,${imagen}`} alt={nombreDriver}
              className="w-10 h-10 rounded-full object-cover border-2 border-indigo-300 cursor-pointer"
              onClick={() => setImagenAmpliada({ src: `data:image/jpeg;base64,${imagen}`, nombre: nombreDriver })} />
          : <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center text-lg">🛵</div>}
        <div>
          <p className="text-xs text-indigo-500 font-medium uppercase tracking-wide">Driver asignado</p>
          <p className="text-sm font-bold text-indigo-800">{nombreDriver}</p>
          {tlfDriver && <p className="text-xs text-indigo-600">📞 {tlfDriver}</p>}
        </div>
      </div>
    );
  };

  // ✅ NUEVO: Widget para mostrar opciones seleccionadas en pedidos
  const OpcionesChips = ({ opcionesJson }) => {
    const grupos = parsearOpciones(opcionesJson);
    if (grupos.length === 0) return null;
    return (
      <div className="mt-1.5 flex flex-wrap gap-1">
        {grupos.map((g, i) => {
          const nombres = (g.opciones ?? []).map(o => o.nombre).join(', ');
          const extra   = (g.opciones ?? []).reduce((s, o) => s + parseFloat(o.precio_adicional ?? 0), 0);
          return (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700">
              <span className="font-semibold">{g.nombre_grupo}:</span>
              <span>{nombres}</span>
              {extra > 0 && <span className="font-bold text-green-600 ml-0.5">+S/{extra.toFixed(2)}</span>}
            </span>
          );
        })}
      </div>
    );
  };

  // ─── MODAL DETALLE PEDIDO ──────────────────────────────────────────────────
  const ModalDetalle = () => {
    if (!pedidoSeleccionado) return null;
    const { venta, detalles } = pedidoSeleccionado;
    const esPendiente   = venta.estatus === 'CREADO';
    const tieneContador = ['ACEPTADO', 'TERMINADO'].includes(venta.estatus) && venta.tiempo > 0;
    const tiempoRestante= tiemposRestantes[venta.id_ventas];
    const tiempoActual  = tiempoPreparacion[venta.id_ventas] || '';
    const estaProcesando= procesando[venta.id_ventas];
    const mostrarDatosClientes = getParametroValue('DATOS_CLIENTES');

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end md:items-center justify-center z-50" onClick={() => setPedidoSeleccionado(null)}>
        <div className="bg-white w-full md:max-w-2xl md:rounded-2xl rounded-t-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="flex justify-center pt-3 pb-1 md:hidden"><div className="w-10 h-1 bg-gray-300 rounded-full"></div></div>

          <div className="flex justify-between items-center px-5 py-4 border-b sticky top-0 bg-white z-10">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Pedido #{venta.id_ventas}</h3>
              <p className="text-xs text-gray-500">{formatearFecha(venta.fecha)} • {formatearHora(venta.hora)}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={cn("px-3 py-1 rounded-full text-xs font-bold border", getEstatusColor(venta.estatus))}>{venta.estatus}</span>
              <button onClick={() => setPedidoSeleccionado(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Cliente */}
            <div className="bg-gray-50 rounded-2xl p-4 space-y-2">
              <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">Cliente</p>
              <p className="font-bold text-gray-900 text-lg">{venta.nombre_cliente}</p>
              {mostrarDatosClientes && venta.tlf_cliente && <p className="text-sm text-gray-600">📞 {venta.tlf_cliente}</p>}
            </div>

            {/* Driver */}
            {venta.nombre_driver && (
              <DriverBadge nombreDriver={venta.nombre_driver} tlfDriver={venta.tlf_driver} imagen={venta.Imagen} compact={false} />
            )}

            {/* Contador */}
            {tieneContador && tiempoRestante && (
              <div className={cn("rounded-2xl p-4 flex items-center justify-between", tiempoRestante.total > 0 ? 'bg-blue-500' : 'bg-red-500 animate-pulse')}>
                <div className="flex items-center gap-2 text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="font-semibold">Tiempo restante</span>
                </div>
                <span className="text-white font-bold text-2xl">{formatearTiempoRestante(tiempoRestante)}</span>
              </div>
            )}

            {/* Productos */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <p className="font-bold text-gray-900">Productos</p>
                <p className="font-bold text-green-600 text-lg">${venta.total_venta?.toFixed(2)}</p>
              </div>
              <div className="space-y-2">
                {detalles?.map((d, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <span className="bg-indigo-600 text-white text-sm font-bold rounded-lg px-2 py-1">{d.cant}x</span>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{d.nombreP}</p>
                        <p className="text-sm text-gray-500">${d.precioU?.toFixed(2)} c/u</p>
                      </div>
                      <p className="font-bold text-gray-800">${d.precioT?.toFixed(2)}</p>
                    </div>
                    {d.notas?.trim() && (
                      <div className="mt-2 ml-10 bg-yellow-100 border-l-4 border-yellow-500 rounded-r-lg p-2">
                        <p className="text-xs font-black text-yellow-800">⚠️ NOTA:</p>
                        <p className="text-sm font-semibold text-gray-800">{d.notas}</p>
                      </div>
                    )}
                    {/* ✅ OPCIONES */}
                    <div className="ml-10">
                      <OpcionesChips opcionesJson={d.opciones_json ?? d.opcionesJson} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Acciones */}
            {esPendiente && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-4 space-y-3">
                <p className="font-semibold text-yellow-800 text-center">⏰ ¿Cuánto tiempo de preparación?</p>
                <div className="flex gap-2">
                  <input type="number" min="1" max="120" placeholder="Minutos" value={tiempoActual}
                    onChange={e => setTiempoPreparacion(prev => ({ ...prev, [venta.id_ventas]: parseInt(e.target.value) || '' }))}
                    className="flex-1 px-4 py-3 rounded-xl border-2 border-yellow-300 text-center text-xl font-bold focus:outline-none focus:border-yellow-500" />
                  <span className="flex items-center px-3 bg-yellow-100 rounded-xl text-yellow-800 font-bold">min</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => handleAceptarPedido(venta.id_ventas)}
                    disabled={estaProcesando || !tiempoActual || tiempoActual <= 0}
                    className={cn("py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2",
                      estaProcesando || !tiempoActual || tiempoActual <= 0 ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-green-500 text-white active:bg-green-700 active:scale-95")}>
                    {estaProcesando ? '⏳' : '✅'} Aceptar
                  </button>
                  <button onClick={() => handleCancelarPedido(venta.id_ventas)} disabled={estaProcesando}
                    className="py-4 rounded-xl font-bold text-lg bg-red-500 text-white active:bg-red-700 active:scale-95">
                    ❌ Cancelar
                  </button>
                </div>
              </div>
            )}

            {venta.estatus === 'ACEPTADO' && (
              <button onClick={() => handleTerminarPedido(venta.id_ventas)} disabled={procesando[venta.id_ventas]}
                className="w-full py-5 rounded-2xl font-bold text-xl bg-green-500 text-white flex items-center justify-center gap-3 active:bg-green-700 active:scale-95 transition-all">
                {procesando[venta.id_ventas] ? '⏳ Procesando...' : '✅ Marcar como Terminado'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── BARRA LÍMITE VENTA ────────────────────────────────────────────────────
  const BarraLimiteVenta = ({ mobile = false }) => {
    if (limiteVenta.limite <= 0) return null;
    const superado   = limiteVenta.consumido > limiteVenta.limite;
    const porcentaje = Math.min((limiteVenta.consumido / limiteVenta.limite) * 100, 100);
    if (mobile) {
      return (
        <div className={cn("rounded-xl p-3 border-2 mt-2", superado ? "bg-red-50 border-red-300" : "bg-green-50 border-green-300")}>
          <div className="flex justify-between items-center mb-2">
            <p className={cn("text-xs font-bold", superado ? "text-red-700" : "text-green-700")}>{superado ? "⚠️ Límite superado" : "💰 Límite de ventas"}</p>
            <p className={cn("text-sm font-bold", superado ? "text-red-600" : "text-green-600")}>${limiteVenta.consumido.toFixed(2)} / ${limiteVenta.limite.toFixed(2)}</p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div className={cn("h-full rounded-full transition-all", superado ? "bg-red-500" : "bg-green-500")} style={{ width: `${porcentaje}%` }}></div>
          </div>
        </div>
      );
    }
    return (
      <div className={cn("mt-6 p-5 rounded-xl shadow-md border-2", superado ? "bg-red-50 border-red-300" : "bg-green-50 border-green-300")}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", superado ? "bg-red-500" : "bg-green-500")}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <h3 className={cn("text-base font-bold", superado ? "text-red-800" : "text-green-800")}>Límite de Ventas</h3>
              <p className={cn("text-xs", superado ? "text-red-600" : "text-green-600")}>{superado ? "⚠️ Límite superado" : "✓ Dentro del límite"}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="text-right"><p className="text-xs text-gray-500 uppercase">Consumido</p><p className={cn("text-xl font-bold", superado ? "text-red-600" : "text-green-600")}>${limiteVenta.consumido.toFixed(2)}</p></div>
            <div className="text-right"><p className="text-xs text-gray-500 uppercase">Límite</p><p className="text-xl font-bold text-gray-800">${limiteVenta.limite.toFixed(2)}</p></div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
          <div className={cn("h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2", superado ? "bg-gradient-to-r from-red-500 to-red-600" : "bg-gradient-to-r from-green-400 to-green-600")} style={{ width: `${porcentaje}%` }}>
            {porcentaje > 15 && <span className="text-white font-bold text-xs">{porcentaje.toFixed(1)}%</span>}
          </div>
        </div>
        <div className="flex justify-between mt-2">
          <p className="text-xs text-gray-600">Disponible: <span className={cn("font-bold", superado ? "text-red-600" : "text-green-600")}>${Math.max(0, limiteVenta.limite - limiteVenta.consumido).toFixed(2)}</span></p>
          {limiteVenta.limiteSecundario > 0 && <p className="text-xs text-gray-600">Límite secundario: <span className="font-bold">${limiteVenta.limiteSecundario.toFixed(2)}</span></p>}
        </div>
      </div>
    );
  };

  // ─── REPORTE ───────────────────────────────────────────────────────────────
  const renderReporte = () => {
    const stats  = reporteData?.estadisticas;
    const ordenes= reporteData?.ordenes || [];
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">📊 Reporte de Órdenes Pasadas</h2>
        <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm font-semibold text-gray-600 mb-3">Seleccionar rango de fechas</p>
          <div className="flex flex-wrap gap-3 items-end">
            <div><label className="text-xs text-gray-500 block mb-1">Desde</label>
              <input type="date" value={reporteFechaInicio} onChange={e => setReporteFechaInicio(e.target.value)}
                className="px-3 py-2 rounded-xl border-2 border-gray-200 focus:border-indigo-400 focus:outline-none text-sm font-medium" /></div>
            <div><label className="text-xs text-gray-500 block mb-1">Hasta</label>
              <input type="date" value={reporteFechaFin} onChange={e => setReporteFechaFin(e.target.value)}
                className="px-3 py-2 rounded-xl border-2 border-gray-200 focus:border-indigo-400 focus:outline-none text-sm font-medium" /></div>
            {[
              { label:'Hoy', fn:()=>{ const h=new Date().toISOString().split('T')[0]; setReporteFechaInicio(h); setReporteFechaFin(h); }},
              { label:'Esta semana', fn:()=>{ const h=new Date(),l=new Date(h); l.setDate(h.getDate()-7); setReporteFechaInicio(l.toISOString().split('T')[0]); setReporteFechaFin(h.toISOString().split('T')[0]); }},
              { label:'Este mes', fn:()=>{ const h=new Date(),l=new Date(h); l.setDate(h.getDate()-30); setReporteFechaInicio(l.toISOString().split('T')[0]); setReporteFechaFin(h.toISOString().split('T')[0]); }},
            ].map(b=>(
              <button key={b.label} onClick={b.fn}
                className="px-3 py-2 rounded-xl border-2 border-indigo-200 text-indigo-700 text-xs font-semibold hover:bg-indigo-50 transition-colors">{b.label}</button>
            ))}
            <button onClick={loadReporte} disabled={loadingReporte}
              className={cn("px-5 py-2 rounded-xl font-bold text-sm text-white transition-all", loadingReporte ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 active:scale-95")}>
              {loadingReporte ? '⏳ Cargando...' : '🔍 Buscar'}</button>
          </div>
        </div>

        {!reporteData && !loadingReporte && (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
            <p className="text-4xl mb-3">📋</p>
            <p className="text-gray-500 font-medium">Selecciona un rango de fechas y presiona Buscar</p>
          </div>
        )}
        {loadingReporte && (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
            <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500">Cargando reporte...</p>
          </div>
        )}

        {reporteData && !loadingReporte && (
          <>
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label:'Total Órdenes',  value: stats.totalOrdenes,                    icon:'🛒' },
                  { label:'Total Ventas',   value:`$${(stats.totalVentas||0).toFixed(2)}`, icon:'💰' },
                                    { label:'Órdenes/Día',    value:(stats.ordenesPorDia||0).toFixed(1),    icon:'📅' },
                ].map(s=>(
                  <div key={s.label} className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
                    <p className="text-2xl mb-1">{s.icon}</p>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{s.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{s.value}</p>
                  </div>
                ))}
              </div>
            )}

            {stats?.ordenesPorFormaPago && Object.keys(stats.ordenesPorFormaPago).length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
                <p className="font-bold text-gray-800 mb-3">💳 Formas de Pago</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.ordenesPorFormaPago).map(([forma,cantidad])=>(
                    <div key={forma} className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-xl border">
                      <span className="font-semibold text-gray-700">{forma}</span>
                      <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">{cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b bg-gray-50 flex justify-between items-center">
                <p className="font-bold text-gray-800">Detalle de Órdenes ({ordenes.length})</p>
              </div>
              {ordenes.length === 0 ? (
                <div className="p-12 text-center"><p className="text-gray-400">No hay órdenes en este período</p></div>
              ) : (
                <div className="divide-y">
                  {ordenes.map(orden => {
                    const expanded = reporteOrdenExpanded === orden.idVentas;
                    return (
                      <div key={orden.idVentas}>
                        <div className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => setReporteOrdenExpanded(expanded ? null : orden.idVentas)}>
                          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-green-700 font-bold text-sm">#{orden.idVentas}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{orden.nombreCliente || 'Sin nombre'}</p>
                            {orden.nombreDriver && (
                              <div className="flex flex-col mt-0.5">
                                <div className="flex items-center gap-1">
                                  <span className="text-xs">🛵</span>
                                  <span className="text-xs text-indigo-600 font-medium">{orden.nombreDriver}</span>
                                </div>
                                {orden.tlfDriver && <span className="text-xs text-indigo-400 pl-4">📞 {orden.tlfDriver}</span>}
                              </div>
                            )}
                          </div>
                          <div className="text-right hidden md:block">
                            <p className="text-sm text-gray-600">{formatearFecha(orden.fecha)}</p>
                            <p className="text-xs text-gray-400">{orden.hora?.substring(0,5)}</p>
                          </div>
                          <div className="hidden lg:block">
                            <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs font-medium text-gray-600">{orden.formaPagoNombre || 'N/A'}</span>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-green-600">${(orden.totalVenta||0).toFixed(2)}</p>
                                          </div>
                          <svg className={cn("w-4 h-4 text-gray-400 flex-shrink-0 transition-transform", expanded && "rotate-180")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                        {expanded && (
                          <div className="px-5 pb-4 bg-gray-50 border-t">
                            <div className="pt-3 space-y-2">
                              <div className="flex flex-wrap gap-3 mb-3 md:hidden">
                                <span className="text-xs text-gray-500">{formatearFecha(orden.fecha)} {orden.hora?.substring(0,5)}</span>
                                {orden.formaPagoNombre && <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">{orden.formaPagoNombre}</span>}
                              </div>
                              {orden.nombreDriver && (
                                <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded-xl mb-2">
                                  <span className="text-base">🛵</span>
                                  <div><p className="text-xs text-indigo-500 font-medium">Driver</p><p className="text-sm font-bold text-indigo-800">{orden.nombreDriver}</p></div>
                                </div>
                              )}
                              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Productos</p>
                              {(orden.detalles || []).length === 0
                                ? <p className="text-sm text-gray-400 italic">Sin productos registrados</p>
                                : (orden.detalles || []).map((d, i) => (
                                  <div key={i} className="bg-white rounded-xl p-2 border">
                                    <div className="flex items-center gap-3">
                                      <span className="bg-indigo-600 text-white text-xs font-bold rounded-lg px-2 py-1 flex-shrink-0">{d.cantidad || d.cant || 1}x</span>
                                      <span className="flex-1 text-sm font-medium text-gray-800">{d.nombreProducto || d.nombreP}</span>
                                      {d.nota && <span className="text-xs text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded">⚠️ {d.nota}</span>}
                                      <span className="text-sm font-bold text-gray-700">${(d.precioTotal || d.precioT || 0).toFixed(2)}</span>
                                    </div>
                                    {/* ✅ OPCIONES en reporte */}
                                    <div className="ml-10 mt-1">
                                      <OpcionesChips opcionesJson={d.opciones_json ?? d.opcionesJson} />
                                    </div>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // ─── MOBILE ────────────────────────────────────────────────────────────────
  const renderPedidosMobile = () => {
    const pendientes = pedidos.filter(p => p.venta.estatus === 'CREADO');
    const enProceso  = pedidos.filter(p => ['ACEPTADO', 'TERMINADO'].includes(p.venta.estatus));
    return (
      <div className="flex flex-col h-full pb-20">
        <audio ref={audioRef} preload="auto"><source src="https://cdn.freesound.org/previews/320/320655_5260872-lq.mp3" type="audio/mpeg" /></audio>
        <div className="bg-white px-4 pt-4 pb-3 border-b sticky top-0 z-10 shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h1 className="text-lg font-bold text-gray-900">{sucursal?.Nombre_sucursal || 'Panel Restaurante'}</h1>
              <p className="text-xs text-gray-500">Pedidos en tiempo real</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { habilitarAudio(); loadPedidos(); }} disabled={loadingPedidos} className="p-2 bg-indigo-100 rounded-xl active:bg-indigo-200">
                <svg className={cn("w-5 h-5 text-indigo-600", loadingPedidos && "animate-spin")} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              </button>
              <div className={cn("flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold", estadoSucursal?.estado === 'ONLINE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
                <div className={cn("w-2 h-2 rounded-full", estadoSucursal?.estado === 'ONLINE' ? 'bg-green-500 animate-pulse' : 'bg-red-500')}></div>
                <select value={estadoSucursal?.estado || 'OFFLINE'} onChange={handleEstadoChange} disabled={isTogglingEstado} className="bg-transparent border-none font-bold text-xs focus:outline-none cursor-pointer">
                  <option value="ONLINE">ONLINE</option><option value="OFFLINE">OFFLINE</option>
                </select>
              </div>
            </div>
          </div>
          {!audioHabilitado && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 flex items-center gap-2">
              <span className="text-lg">🔔</span>
              <p className="text-xs text-blue-700 font-medium">Toca cualquier parte para activar el sonido</p>
            </div>
          )}
          <BarraLimiteVenta mobile={true} />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {pendientes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base font-bold text-yellow-700">🔔 Nuevos pedidos</span>
                <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">{pendientes.length}</span>
              </div>
              <div className="space-y-3">
                {pendientes.map(({ venta, detalles }) => (
                  <div key={venta.id_ventas} onClick={() => { habilitarAudio(); setPedidoSeleccionado({ venta, detalles }); }}
                    className="bg-white border-2 border-yellow-400 rounded-2xl p-4 shadow-md active:scale-95 transition-transform cursor-pointer">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center animate-pulse">
                          <span className="text-white font-bold text-sm">#{venta.id_ventas}</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{venta.nombre_cliente}</p>
                          <p className="text-xs text-gray-500">{formatearHora(venta.hora)}</p>
                          {venta.nombre_driver && <DriverBadge nombreDriver={venta.nombre_driver} tlfDriver={venta.tlf_driver} compact={true} />}
                        </div>
                      </div>
                      <p className="font-bold text-green-600 text-lg">${venta.total_venta?.toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-2 mb-3">
                      <p className="text-xs text-gray-500 mb-1">{detalles?.length} producto(s)</p>
                      <p className="text-sm text-gray-700 truncate">{detalles?.map(d => `${d.cant}x ${d.nombreP}`).join(', ')}</p>
                      {detalles?.some(d => d.notas?.trim()) && <p className="text-xs text-yellow-700 font-bold mt-1">⚠️ Tiene notas especiales</p>}
                      {/* ✅ OPCIONES indicador mobile */}
                      {detalles?.some(d => d.opciones_json || d.opcionesJson) && (
                        <p className="text-xs text-blue-600 font-bold mt-1">🔧 Tiene opciones seleccionadas</p>
                      )}
                    </div>
                    <div className="bg-yellow-50 rounded-xl p-2 text-center">
                      <p className="text-yellow-700 font-semibold text-sm">👆 Toca para aceptar o cancelar</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {enProceso.length > 0 && (
            <div>
              <p className="text-base font-bold text-gray-700 mb-2">⏱️ En preparación</p>
              <div className="space-y-3">
                {enProceso.map(({ venta, detalles }) => {
                  const tiempoRestante = tiemposRestantes[venta.id_ventas];
                  return (
                    <div key={venta.id_ventas} onClick={() => { habilitarAudio(); setPedidoSeleccionado({ venta, detalles }); }}
                      className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm active:scale-95 transition-transform cursor-pointer">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", venta.estatus === 'ACEPTADO' ? 'bg-blue-500' : 'bg-purple-500')}>
                            <span className="text-white font-bold text-sm">#{venta.id_ventas}</span>
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{venta.nombre_cliente}</p>
                            <span className={cn("text-xs font-bold px-2 py-0.5 rounded-full border", getEstatusColor(venta.estatus))}>{venta.estatus}</span>
                            {venta.nombre_driver && <div className="mt-1"><DriverBadge nombreDriver={venta.nombre_driver} tlfDriver={venta.tlf_driver} compact={true} /></div>}
                          </div>
                        </div>
                        {tiempoRestante && (
                          <div className={cn("px-3 py-2 rounded-xl font-bold text-lg", tiempoRestante.total > 0 ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700 animate-pulse')}>
                            {formatearTiempoRestante(tiempoRestante)}
                          </div>
                        )}
                      </div>
                      {venta.estatus === 'ACEPTADO' && (
                        <button onClick={e => { e.stopPropagation(); handleTerminarPedido(venta.id_ventas); }} disabled={procesando[venta.id_ventas]}
                          className="w-full mt-3 py-3 bg-green-500 text-white rounded-xl font-bold text-base active:bg-green-700 active:scale-95 transition-all">
                          {procesando[venta.id_ventas] ? '⏳ Procesando...' : '✅ Marcar Terminado'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {pedidos.length === 0 && !loadingPedidos && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-6xl mb-4">🍽️</p>
              <p className="text-lg font-bold text-gray-700">Sin pedidos activos</p>
              <p className="text-sm text-gray-500 mt-1">Los nuevos pedidos aparecerán aquí</p>
            </div>
          )}
          {loadingPedidos && pedidos.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-gray-500">Cargando pedidos...</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── DESKTOP ───────────────────────────────────────────────────────────────
  const renderPedidosDesktop = () => {
    const mostrarDatosClientes = getParametroValue('DATOS_CLIENTES');
    return (
      <div className="space-y-6">
        <audio ref={audioRef} preload="auto"><source src="https://cdn.freesound.org/previews/320/320655_5260872-lq.mp3" type="audio/mpeg" /></audio>
        {!audioHabilitado && (
          <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg flex items-center gap-3">
            <p className="text-sm text-blue-800 font-medium">💡 Haz clic en cualquier parte para activar las notificaciones de sonido</p>
          </div>
        )}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Pedidos Activos</h2>
          <div className="flex items-center gap-3">
            {pedidos.filter(p => p.venta.estatus === 'CREADO').length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-100 border-2 border-yellow-300 animate-pulse">
                <span className="font-semibold text-yellow-800">{pedidos.filter(p => p.venta.estatus === 'CREADO').length} Pendientes</span>
              </div>
            )}
            <button onClick={() => { habilitarAudio(); loadPedidos(); }} disabled={loadingPedidos}
              className={cn("flex items-center gap-2 px-4 py-2 rounded-lg", theme.classes.button.primary, loadingPedidos && "opacity-50")}>
              <svg className={cn("w-5 h-5", loadingPedidos && "animate-spin")} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              {loadingPedidos ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
        </div>

        {loadingPedidos && pedidos.length === 0 ? (
          <div className={cn(theme.classes.card.base, "p-12 text-center")}><p className="text-gray-500">Cargando pedidos...</p></div>
        ) : pedidos.length === 0 ? (
          <div className={cn(theme.classes.card.base, "p-12 text-center")}>
            <p className="text-lg font-semibold text-gray-700">No hay pedidos activos</p>
            <p className="text-sm text-gray-500 mt-1">Los nuevos pedidos aparecerán aquí automáticamente</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {pedidos.map((pedido) => {
              const { venta, detalles } = pedido;
              const esPendiente   = venta.estatus === 'CREADO';
              const tieneContador = ['ACEPTADO', 'TERMINADO'].includes(venta.estatus) && venta.tiempo > 0;
              const tiempoRestante= tiemposRestantes[venta.id_ventas];
              const tiempoActual  = tiempoPreparacion[venta.id_ventas] || '';
              const estaProcesando= procesando[venta.id_ventas];

              return (
                <div key={venta.id_ventas}
                  className={cn(theme.classes.card.base, theme.classes.card.hover, "p-6 cursor-pointer transition-all duration-200", esPendiente && "border-2 border-yellow-400 shadow-lg")}
                  onClick={() => setPedidoSeleccionado(pedido)}>
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className={cn(theme.classes.iconBox.primary, "w-16 h-16 flex flex-col items-center justify-center", esPendiente && "bg-yellow-500 animate-pulse")}>
                            <span className="text-2xl font-bold text-white">#{venta.id_ventas}</span>
                          </div>
                          <div className="space-y-1">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">{venta.nombre_cliente}</h3>
                              {mostrarDatosClientes && venta.tlf_cliente && <p className="text-sm text-gray-600">📞 {venta.tlf_cliente}</p>}
                            </div>
                            {venta.nombre_driver && (
                              <DriverBadge nombreDriver={venta.nombre_driver} tlfDriver={venta.tlf_driver} imagen={venta.Imagen} compact={true} />
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border-2", getEstatusColor(venta.estatus))}>{venta.estatus}</span>
                          {detalles?.some(d => d.notas?.trim()) && (
                            <div className="px-3 py-1.5 bg-blue-100 border-2 border-blue-400 rounded-lg animate-pulse">
                              <span className="text-xs font-bold text-blue-700">⚠️ TIENE NOTAS</span>
                            </div>
                          )}
                          {/* ✅ Badge opciones */}
                          {detalles?.some(d => d.opciones_json || d.opcionesJson) && (
                            <div className="px-3 py-1.5 bg-blue-50 border border-blue-300 rounded-lg">
                              <span className="text-xs font-bold text-blue-600">🔧 CON OPCIONES</span>
                            </div>
                          )}
                          {tieneContador && tiempoRestante && (
                            <div className="flex flex-col gap-2">
                              <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold text-lg", tiempoRestante.total > 0 ? 'bg-blue-500 text-white' : 'bg-red-500 text-white animate-pulse')}>
                                <span>{formatearTiempoRestante(tiempoRestante)}</span>
                              </div>
                              {venta.estatus === 'ACEPTADO' && (
                                <button onClick={e => { e.stopPropagation(); handleTerminarPedido(venta.id_ventas); }} disabled={estaProcesando}
                                  className="px-5 py-3 rounded-lg font-semibold bg-green-500 hover:bg-green-600 text-white">
                                  {estaProcesando ? 'Procesando...' : '✅ Terminado'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div><p className="text-xs text-gray-500 uppercase">Fecha</p><p className="font-semibold">{formatearFecha(venta.fecha)}</p></div>
                        <div><p className="text-xs text-gray-500 uppercase">Hora</p><p className="font-semibold">{formatearHora(venta.hora)}</p></div>
                        <div><p className="text-xs text-gray-500 uppercase">Items</p><p className="font-semibold">{detalles?.length || 0}</p></div>
                      </div>

                      {detalles?.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-3 border">
                          <div className="flex justify-between mb-2">
                            <p className="text-sm font-bold text-gray-700">Productos:</p>
                            <p className="font-bold text-green-600">${venta.total_venta?.toFixed(2)}</p>
                          </div>
                          <div className="space-y-2">
                            {detalles.map((d, i) => (
                              <div key={i}>
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-1 bg-indigo-600 text-white text-xs font-bold rounded">{d.cant}x</span>
                                  <span className="font-medium text-gray-900">{d.nombreP}</span>
                                  <span className="text-sm text-gray-600">${d.precioT?.toFixed(2)}</span>
                                </div>
                                {d.notas?.trim() && (
                                  <div className="ml-10 mt-1 bg-yellow-200 border-l-4 border-yellow-600 rounded-r p-2">
                                    <p className="text-xs font-black text-yellow-900">⚠️ NOTA:</p>
                                    <p className="text-sm font-bold">{d.notas}</p>
                                  </div>
                                )}
                                {/* ✅ OPCIONES desktop card */}
                                <div className="ml-10 mt-1">
                                  <OpcionesChips opcionesJson={d.opciones_json ?? d.opcionesJson} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {esPendiente && (
                      <div className="lg:w-80 flex flex-col gap-3 p-4 rounded-lg bg-yellow-50 border-2 border-yellow-300" onClick={e => e.stopPropagation()}>
                        <p className="font-semibold text-yellow-800">⏰ Tiempo de Preparación</p>
                        <div className="flex gap-2">
                          <input type="number" min="1" max="120" placeholder="Minutos" value={tiempoActual}
                            onChange={e => setTiempoPreparacion(prev => ({ ...prev, [venta.id_ventas]: parseInt(e.target.value) || '' }))}
                            className="flex-1 px-4 py-3 rounded-lg border-2 border-yellow-300 focus:outline-none text-center text-lg font-semibold" />
                          <span className="flex items-center px-3 bg-yellow-100 rounded-lg text-yellow-800 font-semibold">min</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={e => { e.stopPropagation(); handleAceptarPedido(venta.id_ventas); }}
                            disabled={estaProcesando || !tiempoActual || tiempoActual <= 0}
                            className={cn("flex-1 py-3 rounded-lg font-semibold", estaProcesando || !tiempoActual ? "bg-gray-300 text-gray-500" : "bg-green-500 hover:bg-green-600 text-white")}>
                            {estaProcesando ? 'Procesando...' : '✅ Aceptar'}
                          </button>
                          <button onClick={e => { e.stopPropagation(); handleCancelarPedido(venta.id_ventas); }} disabled={estaProcesando}
                            className="flex-1 py-3 rounded-lg font-semibold bg-red-500 hover:bg-red-600 text-white">
                            ❌ Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'reporte':     return renderReporte();
      case 'categoria':   return <CategoriaList sucursalId={sucursalId}/>;
      case 'producto':    return (
        <ProductoList
          sucursalId={sucursalId}
          onVerOpciones={(p) => setOpcionesModal(p)}  // ✅ NUEVO
        />
      );
      case 'pagocliente': return <PagoClienteList sucursalId={sucursalId}/>;
      case 'descuento':   return <DescuentoList sucursalId={sucursalId}/>;
      default:            return isMobile ? renderPedidosMobile() : renderPedidosDesktop();
    }
  };

  // ─── LAYOUT MOBILE ─────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
        <div className="flex-1 overflow-hidden">
          {activeSection === 'home'
            ? renderPedidosMobile()
            : activeSection === 'reporte'
            ? <div className="h-full overflow-y-auto p-4">{renderReporte()}</div>
            : (
              <div className="h-full overflow-y-auto">
                <div className="flex items-center gap-3 px-4 py-3 bg-white border-b">
                  <button onClick={() => setActiveSection('home')} className="p-2 hover:bg-gray-100 rounded-xl">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <h2 className="font-bold text-gray-900">{menuItems.find(m => m.id === activeSection)?.label}</h2>
                </div>
                <div className="p-4">{renderContent()}</div>
              </div>
            )
          }
        </div>
        <div className="bg-white border-t border-gray-200 px-2 py-2">
          <div className="flex justify-around items-center">
            {menuItems.map(item => (
              <button key={item.id} onClick={() => handleMenuClick(item.id, item.bloqueado)}
                className={cn("flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all min-w-0 relative",
                  activeSection === item.id ? "bg-indigo-100 text-indigo-700" : "text-gray-500",
                  item.bloqueado && "opacity-40")}>
                <span className="text-xl">{item.icon}</span>
                <span className={cn("text-xs font-medium truncate", activeSection === item.id && "font-bold")}>{item.label}</span>
                {item.id === 'home' && pedidos.filter(p => p.venta.estatus === 'CREADO').length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {pedidos.filter(p => p.venta.estatus === 'CREADO').length}
                  </span>
                )}
              </button>
            ))}
            <button onClick={handleLogout} className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl text-red-500">
              <span className="text-xl">🚪</span><span className="text-xs font-medium">Salir</span>
            </button>
          </div>
        </div>
        <ModalDetalle />
        {/* ✅ MODAL OPCIONES */}
        {opcionesModal && <ProductoOpcionesModal producto={opcionesModal} onClose={() => setOpcionesModal(null)} />}
        {imagenAmpliada && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4" onClick={() => setImagenAmpliada(null)}>
            <img src={imagenAmpliada.src} alt={imagenAmpliada.nombre} className="max-w-full max-h-[80vh] rounded-2xl" />
          </div>
        )}
        <button onClick={handleWhatsAppClick}
          className="fixed bottom-20 right-4 w-12 h-12 bg-green-500 text-white rounded-full shadow-xl flex items-center justify-center z-40 animate-pulse hover:animate-none">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
        </button>
      </div>
    );
  }

  // ─── LAYOUT DESKTOP ────────────────────────────────────────────────────────
  return (
    <div className={cn("flex min-h-screen", theme.classes.background.main)}>
      <div className={cn(theme.classes.sidebar.container, theme.shadows['2xl'], theme.transitions.default, sidebarOpen ? 'w-72' : 'w-20', 'flex flex-col')}>
        <div className={cn("p-6", theme.classes.sidebar.header, "border-b")}>
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex flex-col items-center w-full">
                <img src={sucursal?.Logo ? `data:image/jpeg;base64,${sucursal.Logo}` : "/images/logo.png"} alt="Logo"
                  className="w-20 h-20 rounded-full mb-3 object-cover shadow-xl border-4 border-white"
                  onError={e => { e.target.src = "/images/logo.png"; }} />
                <h1 className="font-bold text-xl text-white">{sucursal?.Nombre_sucursal || 'Sistema Admin'}</h1>
                <p className="text-sm text-gray-300 mt-1">Panel de Control</p>
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-gray-800 rounded-xl text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map(item => {
            const bloqueado = item.bloqueado || false;
            return (
              <button key={item.id} onClick={() => handleMenuClick(item.id, bloqueado)} disabled={bloqueado}
                className={cn("w-full flex items-center p-4 rounded-xl", theme.transitions.default,
                  bloqueado && "opacity-50 cursor-not-allowed",
                  activeSection === item.id && !bloqueado ? theme.classes.sidebar.button.active : theme.classes.sidebar.button.inactive)}>
                <span className="text-xl">{item.icon}</span>
                {sidebarOpen && <span className="ml-4 font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>
        <div className={cn("p-4", theme.classes.sidebar.header, "border-t")}>
          {sidebarOpen && user && (
            <div className={cn(theme.classes.sidebar.userCard, "mb-4 p-4")}>
              <p className="text-sm font-medium text-white">{user.usuarioNombre}</p>
              <p className="text-xs text-gray-300">{user.tipo}</p>
            </div>
          )}
          <button onClick={handleLogout} className={cn("w-full flex items-center p-4 rounded-xl", theme.classes.sidebar.logout, theme.transitions.default)}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            {sidebarOpen && <span className="ml-4 font-medium">Cerrar Sesión</span>}
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <header className={theme.classes.header.container}>
          <div className="px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className={theme.classes.header.title}>{menuItems.find(m => m.id === activeSection)?.label || 'Dashboard'}</h2>
                <p className={cn(theme.classes.header.subtitle, "mt-1")}>Gestión y administración</p>
              </div>
              <div className="flex items-center gap-4">
                <div className={cn("flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm", estadoSucursal?.estado === 'ONLINE' ? theme.classes.status.online : theme.classes.status.offline)}>
                  <div className={cn("w-2.5 h-2.5 rounded-full", estadoSucursal?.estado === 'ONLINE' ? 'bg-white animate-pulse' : 'bg-white')}></div>
                  <select value={estadoSucursal?.estado || 'OFFLINE'} onChange={handleEstadoChange} disabled={isTogglingEstado}
                    className="bg-transparent border-none font-semibold text-sm focus:outline-none cursor-pointer text-white">
                    <option value="ONLINE" className="text-green-700 bg-green-50">ONLINE</option>
                    <option value="OFFLINE" className="text-gray-700 bg-gray-50">OFFLINE</option>
                  </select>
                </div>
                <p className="text-sm font-medium text-gray-900">{user?.usuarioNombre}</p>
                <button onClick={handleLogout} className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                </button>
              </div>
            </div>
            {activeSection === 'home' && <BarraLimiteVenta />}
          </div>
        </header>
        <main className="flex-1 p-8 overflow-auto">{renderContent()}</main>
        <button onClick={handleWhatsAppClick}
          className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-2xl flex items-center justify-center z-50 animate-pulse hover:animate-none">
          <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
        </button>
      </div>

      <ModalDetalle />
      {/* ✅ MODAL OPCIONES */}
      {opcionesModal && <ProductoOpcionesModal producto={opcionesModal} onClose={() => setOpcionesModal(null)} />}
      {imagenAmpliada && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4" onClick={() => setImagenAmpliada(null)}>
          <img src={imagenAmpliada.src} alt={imagenAmpliada.nombre} className="max-w-full max-h-[80vh] rounded-2xl" />
        </div>
      )}
    </div>
  );
};

export default Dashboard;